import { authApi } from '@/api/auth'
import { ApiError } from '@/api/errors'
import type { MeResponse, User } from '@/api/types'
import { useAuthStore } from '@/auth/store'

/** Ne conserve que les champs `User` : `client` et `auth` ne sont pas persistés. */
export function pickUser(me: MeResponse): User {
  const { client: _client, auth: _auth, ...user } = me
  return user
}

let bootstrap: Promise<void> | null = null

/**
 * Au-delà de ce délai, l'affichage cesse d'attendre `GET /auth/me`.
 * Les gardes de routes attendent cette promesse avant de monter quoi que ce
 * soit : sans plafond, une API qui accepte la connexion TCP mais ne répond
 * jamais laisse l'application sur son écran de chargement indéfiniment.
 */
const RESOLVE_TIMEOUT_MS = 3_000

/**
 * Revalide la session relue depuis `localStorage` via `GET /auth/me`.
 * Résout immédiatement si le statut est déjà connu ; une seule requête en vol.
 * Rend la main au bout de `RESOLVE_TIMEOUT_MS` quoi qu'il arrive — la
 * revalidation, elle, se poursuit en arrière-plan et corrigera le statut.
 */
export function ensureSessionResolved(): Promise<void> {
  if (useAuthStore.getState().status !== 'loading') return Promise.resolve()

  bootstrap ??= (async () => {
    try {
      const me = await authApi.me()
      const store = useAuthStore.getState()
      store.setUser(pickUser(me))
      store.resolve('authenticated')
    } catch (error) {
      if (error instanceof ApiError && error.isAuthFailure) {
        // Jetons expirés ou révoqués : on repart d'une session anonyme.
        useAuthStore.getState().signOut()
      } else {
        // Panne réseau ou erreur serveur : la session locale reste valide,
        // on évite de déconnecter quelqu'un parce que l'API est momentanément
        // injoignable. L'utilisateur est considéré connecté et les requêtes
        // suivantes retenteront.
        useAuthStore.getState().resolve('authenticated')
      }
    } finally {
      bootstrap = null
    }
  })()

  return raceWithTimeout(bootstrap)
}

/**
 * Laisse la revalidation continuer, mais débloque le rendu au bout du délai.
 * On applique alors la même politique que pour une panne réseau : la session
 * locale est présumée valide plutôt que d'éjecter quelqu'un parce que l'API
 * tarde. Un vrai 401 arrivera de toute façon à la première requête utile.
 */
function raceWithTimeout(pending: Promise<void>): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(() => {
      if (useAuthStore.getState().status === 'loading') {
        useAuthStore.getState().resolve('authenticated')
      }
      // La requête en cours cesse de faire autorité : sans cela, un `me()` qui
      // ne répond jamais resterait mémorisé ici et interdirait toute nouvelle
      // tentative de revalidation pour le reste de la session.
      bootstrap = null
      resolve()
    }, RESOLVE_TIMEOUT_MS)

    void pending.finally(() => {
      clearTimeout(timer)
      resolve()
    })
  })
}

/** Déconnexion : on prévient le backend au mieux, puis on vide l'état local. */
export async function signOutEverywhere(all = false): Promise<void> {
  const { refreshToken, signOut } = useAuthStore.getState()
  try {
    if (refreshToken) await authApi.logout(refreshToken, all)
  } catch {
    /* Le backend est peut-être injoignable : la session locale est vidée quand même. */
  } finally {
    signOut()
  }
}
